// The day, while it is happening.
//
// One map and one column, and the column is sorted by *who needs the
// dispatcher* rather than by time: a round with a door the driver could
// not deliver first, then anything that has gone quiet, then the ones
// working. A board sorted by when it was created is a board where the
// problem is wherever the problem happens to be.
//
// Everything here is the runs' own signed records. This console holds
// their links because it minted them and subscribes like any other
// holder, so there is no privileged view — and nothing is drawn that a
// run did not say. A vehicle with no live position claim has no pin, and
// a round nobody is publishing to reads as silence rather than as
// "waiting", because those are different facts.

import { useCallback, useMemo, useState } from "react";
import {
  STOP_OUTCOME,
  THREAD_STATE,
  stopProgress,
  type FollowSnapshot,
  type StopOutcomeCode
} from "@wayfind/core";
import { Note, Panel } from "./Bits";
import { FleetMap, type MapLeg, type MapStop, type MapVehicle } from "./FleetMap";
import { DayBar, type DayLane } from "./DayBar";
import { Feed } from "./Feed";
import { useRemainingRoads, type RoadRequest } from "../ops/leg";
import { useRunEvents } from "../ops/events";
import type { RouteEngine } from "@wayfind/core";
import type { FleetState, StoredJob } from "../ops/state";
import { clockWords, plural, type Vocabulary } from "../ops/words";
import { nowSeconds } from "../config";

const DAY = 86400;

/** How much this round wants attention. Lower sorts first. */
function urgency(snapshot: FollowSnapshot | null): number {
  if (!snapshot) return 3;
  const { status } = snapshot;
  const missed = status.outcomes.some(
    outcome => outcome === STOP_OUTCOME.SKIPPED || outcome === STOP_OUTCOME.FAILED
  );
  if (missed) return 0;
  // Heard once and then nothing is the shape of a phone that died in a
  // basement, and it is the thing a dispatcher finds out about too late.
  if (status.observed.ever && status.observed.stale) return 1;
  if (!status.observed.ever) return 2;
  return 3;
}

export function Live({
  state,
  jobs,
  live,
  engine,
  vocabulary,
  onOpen
}: {
  state: FleetState;
  /** Today's jobs, already filtered by the shell. */
  jobs: StoredJob[];
  live: Map<string, FollowSnapshot>;
  engine: RouteEngine | null;
  vocabulary: Vocabulary;
  onOpen(job: StoredJob): void;
}): JSX.Element {
  const [focused, setFocused] = useState<string | null>(null);
  const now = nowSeconds();

  const open = useMemo(
    () => jobs
      .filter(job => job.finishedUnixSeconds == null)
      .slice()
      .sort((a, b) => {
        const byUrgency = urgency(live.get(a.id) ?? null) - urgency(live.get(b.id) ?? null);
        if (byUrgency !== 0) return byUrgency;
        return b.createdUnixSeconds - a.createdUnixSeconds;
      }),
    [jobs, live]
  );
  const closed = useMemo(
    () => jobs.filter(job => job.finishedUnixSeconds != null),
    [jobs]
  );

  const vehicles: MapVehicle[] = [];
  const stops: MapStop[] = [];
  for (const job of open) {
    const snapshot = live.get(job.id) ?? null;
    const status = snapshot?.status ?? null;
    job.stops.forEach((stop, index) => {
      const outcome = (status?.outcomes[index] ?? STOP_OUTCOME.PENDING) as StopOutcomeCode;
      stops.push({
        id: `${job.id}-${stop.id}`,
        lat: stop.lat,
        lon: stop.lon,
        label: stop.label,
        tone: outcome === STOP_OUTCOME.DELIVERED
          ? "delivered"
          : outcome === STOP_OUTCOME.PENDING ? "pending" : "missed",
        focused: focused === job.id
      });
    });
    if (snapshot?.position) {
      vehicles.push({
        id: job.id,
        lat: snapshot.position.lat,
        lon: snapshot.position.lon,
        label: state.devices.find(device => device.id === job.deviceId)?.name
          ?? job.driverName
          ?? "The vehicle",
        focused: focused === job.id
      });
    }
  }

  // Every round out gets its remaining road routed — the board draws all
  // of them as track, and only the selected one's current leg in amber.
  // `open` is already sorted by who needs the dispatcher, so if the cap
  // in `useRemainingRoads` bites, what it drops is what the board itself
  // ranked last.
  const requests: RoadRequest[] = useMemo(
    () => open.map(job => {
      const outcomes = live.get(job.id)?.status.outcomes ?? [];
      return {
        id: job.id,
        from: live.get(job.id)?.position ?? null,
        doors: job.stops
          .filter((_, index) => (outcomes[index] ?? STOP_OUTCOME.PENDING) === STOP_OUTCOME.PENDING)
          .map(stop => ({ lat: stop.lat, lon: stop.lon }))
      };
    }),
    [open, live]
  );
  const roads = useRemainingRoads(engine, requests);

  // Which round the amber belongs to. Nothing is selected until somebody
  // selects something, so the board opens on the round it sorted to the
  // top — the one that most needs a dispatcher.
  const selectedId = focused ?? open[0]?.id ?? null;

  const legs: MapLeg[] = open
    .filter(job => roads[job.id]?.track.length)
    .map(job => ({
      id: job.id,
      track: roads[job.id]!.track,
      range: roads[job.id]!.range,
      selected: job.id === selectedId
    }));

  // The chevron is a leg's own bearing, so each van points along the
  // road its own round is driving.
  for (const vehicle of vehicles) {
    vehicle.headingDegrees = roads[vehicle.id]?.headingDegrees ?? null;
  }

  // What changed while the dispatcher was doing something else. Watches
  // every job today, not just the open ones, so a round finishing is
  // still reported as it leaves the board.
  const nameFor = useCallback(
    (job: StoredJob) => state.devices.find(device => device.id === job.deviceId)?.name
      ?? job.driverName
      ?? (job.heldBy ? "This console" : "Nobody yet"),
    [state.devices]
  );
  const events = useRunEvents(jobs, live, nameFor);

  const lanes: DayLane[] = open.map(job => ({
    job,
    snapshot: live.get(job.id) ?? null,
    road: roads[job.id],
    who: state.devices.find(device => device.id === job.deviceId)?.name
      ?? job.driverName
      ?? (job.heldBy ? "This console" : "Nobody yet")
  }));

  const trouble = open.filter(job => urgency(live.get(job.id) ?? null) === 0).length;
  const quiet = open.filter(job => urgency(live.get(job.id) ?? null) === 1).length;

  return (
    <div className="livepage">
      {/*
        The summary before the detail, and first in source order so it is
        also the first thing reached by a keyboard: whether the day
        finishes is the question, and the map and the board are how you
        go and look at whichever lane answered it badly.
      */}
      {open.length ? (
        <div className="livepage__day">
          <DayBar
            lanes={lanes}
            selected={selectedId}
            vocabulary={vocabulary}
            onSelect={setFocused}
            onOpen={onOpen}
          />
        </div>
      ) : null}

      <div className="livepage__map">
        <FleetMap
          engine={engine}
          vehicles={vehicles}
          stops={stops}
          legs={legs}
          onPick={id => { setFocused(id); }}
        />
        <div className="livepage__legend">
          <span className="legend"><i className="legend__dot legend__dot--van" />publishing a position</span>
          <span className="legend"><i className="legend__dot legend__dot--pending" />{vocabulary.stop}s still to do</span>
          <span className="legend"><i className="legend__dot legend__dot--delivered" />delivered</span>
          <span className="legend"><i className="legend__dot legend__dot--missed" />not delivered</span>
        </div>
      </div>

      <div className="livepage__rail stack">
        {trouble || quiet ? (
          <Note
            title={[
              trouble ? `${trouble} ${trouble === 1 ? "round has" : "rounds have"} a ${vocabulary.stop} that did not get delivered` : "",
              quiet ? `${quiet} ${quiet === 1 ? "has" : "have"} gone quiet` : ""
            ].filter(Boolean).join(" · ")}
            tone={trouble ? "halt" : "signal"}
          >
            The {vocabulary.recipient} on that link was told at the same moment you were — this is
            the same signed record, not a copy of it.
          </Note>
        ) : null}

        <Panel>
          {/*
            The board's one loud number. Six sizes ship in the brand scale
            and this console spent all day at two of them, which is how a
            screen ends up with no entry point — the eye starts top-left
            and reads everything. How many are out is the question this
            page is opened for, so it gets to be the answer.
          */}
          <div className="boardhead">
            <span className="boardhead__count wf-numeric">{open.length}</span>
            <span className="boardhead__label">
              {open.length === 1 ? "round out now" : "rounds out now"}
            </span>
          </div>
          {open.length ? (
            <div className="runlist">
              {open.map(job => (
                <RunCard
                  key={job.id}
                  job={job}
                  state={state}
                  snapshot={live.get(job.id) ?? null}
                  vocabulary={vocabulary}
                  focused={focused === job.id}
                  onFocus={() => setFocused(job.id)}
                  onOpen={() => onOpen(job)}
                />
              ))}
            </div>
          ) : (
            <p className="small muted">
              Nothing is out. What you send appears here the moment a {vocabulary.driver} takes it.
            </p>
          )}
        </Panel>

        <Feed events={events} onPick={setFocused} />

        {closed.length ? (
          <Panel eyebrow={`Finished today · ${closed.length}`}>
            <div className="runlist">
              {closed.map(job => (
                <RunCard
                  key={job.id}
                  job={job}
                  state={state}
                  snapshot={live.get(job.id) ?? null}
                  vocabulary={vocabulary}
                  focused={false}
                  onFocus={() => {}}
                  onOpen={() => onOpen(job)}
                />
              ))}
            </div>
          </Panel>
        ) : null}

        {now - (jobs[0]?.createdUnixSeconds ?? now) > DAY ? null : null}
      </div>
    </div>
  );
}

/**
 * One round, as a card a dispatcher can read at a glance from across a
 * desk: who, how far through, and whether anything is wrong.
 */
function RunCard({
  job,
  state,
  snapshot,
  vocabulary,
  focused,
  onFocus,
  onOpen
}: {
  job: StoredJob;
  state: FleetState;
  snapshot: FollowSnapshot | null;
  vocabulary: Vocabulary;
  focused: boolean;
  onFocus(): void;
  onOpen(): void;
}): JSX.Element {
  const status = snapshot?.status ?? null;
  const progress = status ? stopProgress(status, snapshot?.plan ?? null) : null;
  const who = state.devices.find(device => device.id === job.deviceId)?.name
    ?? job.driverName
    ?? (job.heldBy ? "This console" : "Nobody yet");
  const ended = status
    ? status.state === THREAD_STATE.COMPLETED || status.state === THREAD_STATE.CANCELED
    : job.finishedUnixSeconds != null;
  const missed = (status?.outcomes ?? []).filter(
    outcome => outcome === STOP_OUTCOME.SKIPPED || outcome === STOP_OUTCOME.FAILED
  ).length;

  const state_ = !status?.observed.ever
    ? { label: ended ? "closed, never published" : "not started", tone: "quiet" }
    : ended
      ? { label: "finished", tone: "settled" }
      : status.observed.stale
        ? { label: `quiet ${Math.round((status.observed.ageSeconds ?? 0) / 60)} min`, tone: "halt" }
        : status.position.kind === "live"
          ? { label: "on the road", tone: "live" }
          : { label: "publishing", tone: "live" };

  const total = progress?.total ?? job.stops.length;
  const done = progress?.delivered ?? 0;

  return (
    /*
      Selecting is a click, not a hover.

      This used to select on `onMouseEnter`, which meant the map's most
      expensive rendering — whose round gets the amber road — was driven
      by a state that does not exist on a depot touchscreen and cannot be
      reached from a keyboard, and that changed every time a cursor
      crossed the list on its way somewhere else. Hover is still a hint,
      but it no longer decides anything.
    */
    <article
      className={`runcard runcard--${state_.tone}${focused ? " runcard--focused" : ""}`}
      onClick={onFocus}
      onFocusCapture={onFocus}
    >
      <button type="button" className="runcard__hit" onClick={onOpen}>
        <span className="runcard__who">{who}</span>
        <span className={`chip chip--${state_.tone === "live" ? "now" : state_.tone}`}>
          <span className="chip__dot" aria-hidden="true" />
          {state_.label}
        </span>
      </button>

      <p className="runcard__what">
        {job.stops.length === 1
          ? job.stops[0]!.label
          : `${job.stops[0]!.label} → ${plural(job.stops.length, vocabulary.stop)}`}
      </p>

      {/*
        A bar, not a percentage. "62%" of a delivery round is a number
        nobody acts on; eleven marks with three filled is a shape you
        read without counting.
      */}
      <div className="pips" aria-label={`${done} of ${total} delivered`}>
        {Array.from({ length: Math.min(total, 24) }, (_, index) => {
          const outcome = (status?.outcomes[index] ?? STOP_OUTCOME.PENDING) as StopOutcomeCode;
          const tone = outcome === STOP_OUTCOME.DELIVERED
            ? "done"
            : outcome === STOP_OUTCOME.PENDING ? "todo" : "missed";
          return <i key={index} className={`pip pip--${tone}`} />;
        })}
      </div>

      <p className="runcard__facts">
        {done}/{total} delivered
        {missed ? ` · ${missed} not` : ""}
        {status?.lastOutcome?.hasPhoto ? " · photo" : ""}
        {` · sent ${clockWords(job.createdUnixSeconds)}`}
      </p>
    </article>
  );
}
